import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { BLOCK_META } from '@ellie/shared';
import type { BlockType } from '@ellie/shared';

interface SortableBlockItem {
  id: string;
  type: BlockType;
  title: string;
}

interface SortableBlockProps {
  block: SortableBlockItem;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

export function SortableBlock({ block, isSelected, onSelect, onDelete }: SortableBlockProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const meta = BLOCK_META[block.type];

  function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    const confirmed = window.confirm(`Delete "${block.title}"? This cannot be undone.`);
    if (confirmed) {
      onDelete(block.id);
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 rounded-lg border bg-white px-3 py-2 transition-colors ${
        isDragging ? 'opacity-50' : ''
      } ${
        isSelected
          ? 'border-l-4 border-l-brand bg-brand-light border-brand'
          : 'border-border hover:border-brand/50'
      }`}
      onClick={() => onSelect(block.id)}
    >
      {/* Drag handle */}
      <button
        className="cursor-grab touch-none text-text-light hover:text-text-muted"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>

      {/* Block type badge */}
      <span
        className="shrink-0 rounded px-2 py-0.5 text-xs font-semibold text-white"
        style={{ backgroundColor: meta.color }}
      >
        {meta.name}
      </span>

      {/* Block title */}
      <span className="flex-1 truncate text-sm text-text-primary">{block.title}</span>

      {/* Delete button (visible on hover) */}
      <button
        onClick={handleDelete}
        className="shrink-0 rounded p-1 text-text-light opacity-0 transition-opacity hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
        title="Delete block"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}
